import React, { SetStateAction, useEffect, useRef, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import {
  getPreScanMemberProfile as fetchPreScanMemberProfile,
  submitPreScanQuestionnaire,
} from '../../../api/preScan';
import { categorizeError, ErrorInfo } from '../../../utils/errorHandler';
import { formatUKDate } from '../../../utils';
import { genders } from '../../../utils/constants';
import { PreScanMemberProfile } from '../../../types/preScanTypes';
import {
  formToScanInfoPayload,
  initialPreScanQuestionnaireFormValues,
  mapScanInfoToForm,
  PreScanQuestionnaireFormValues,
  validatePreScanQuestionnaireForm,
} from '../../../types/preScanQuestionnaire';
import { toast } from 'react-toastify';
import Loader from '../../BaseComponents/Loader';
import PreScanQuestionnaireFormFields from './PreScanQuestionnaireFormFields';

interface PreScanQuestionnaireFormProps {
  setIsSubmitted: React.Dispatch<SetStateAction<boolean>>;
  onError: (errorInfo: ErrorInfo | null) => void;
}

const getTokenFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('token');
};

const formatGenderLabel = (gender?: string | null) => {
  if (!gender) return '-';
  const match = genders.find((item) => item.key === gender);
  if (match) return match.value;
  return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
};

const PreScanQuestionnaireForm: React.FC<PreScanQuestionnaireFormProps> = ({
  setIsSubmitted,
  onError,
}) => {
  const [memberData, setMemberData] = useState<PreScanMemberProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [formInitialValues, setFormInitialValues] = useState<PreScanQuestionnaireFormValues>(
    initialPreScanQuestionnaireFormValues()
  );
  const hasFetchedProfile = useRef(false);

  useEffect(() => {
    if (hasFetchedProfile.current) {
      return;
    }
    hasFetchedProfile.current = true;

    const fetchMemberData = async () => {
      setIsLoadingProfile(true);
      try {
        const profile = await fetchPreScanMemberProfile();
        const membershipType = profile.membership_type?.toLowerCase();
        if (membershipType && membershipType !== 'plus' && membershipType !== 'scan') {
          onError(
            categorizeError({
              response: {
                status: 403,
                data: {
                  detail:
                    'Pre-scan questionnaire is only available for Plus and Scan members.',
                },
              },
            })
          );
          return;
        }

        setMemberData(profile);

        const scanInfo: Record<string, unknown> | null = profile.scan_info || null;

        if (scanInfo?.pre_scan_questionnaire_completed_at) {
          setIsSubmitted(true);
        }

        const storedConfirmations = scanInfo?.disclaimer_confirmations;
        setFormInitialValues({
          ...mapScanInfoToForm(scanInfo),
          disclaimer_confirmations:
            storedConfirmations && typeof storedConfirmations === 'object'
              ? (storedConfirmations as Record<string, boolean>)
              : initialPreScanQuestionnaireFormValues(profile.gender).disclaimer_confirmations,
        });
        onError(null);
      } catch (error: any) {
        onError(categorizeError(error));
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchMemberData();
  }, [onError, setIsSubmitted]);

  const handleSubmit = async (
    values: PreScanQuestionnaireFormValues,
    { setSubmitting }: FormikHelpers<PreScanQuestionnaireFormValues>
  ) => {
    const token = getTokenFromURL();
    if (!token) {
      toast.error('Invalid or missing token.');
      setSubmitting(false);
      return;
    }

    try {
      const { disclaimer_confirmations, ...formData } = values;
      const payload = formToScanInfoPayload(
        formData,
        disclaimer_confirmations,
        memberData?.gender
      );
      const response = await submitPreScanQuestionnaire(token, payload);

      if (response?.status === 'success') {
        setIsSubmitted(true);
        toast.success('Pre-scan questionnaire submitted successfully.');
      } else {
        toast.error('Unable to submit questionnaire. Please try again.');
      }
    } catch (error: any) {
      const message =
        error?.detail || error?.message || 'Unable to submit questionnaire. Please try again.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoadingProfile) {
    return <Loader height='200px' />;
  }

  const fullName = memberData
    ? `${memberData.first_name || ''} ${memberData.last_name || ''}`.trim() || '-'
    : '-';
  const dateOfBirth = formatUKDate(memberData?.dob);
  const gender = formatGenderLabel(memberData?.gender);
  const phoneNumber = memberData?.phone || '-';
  const email = memberData?.email || '-';

  return (
    <Formik
      enableReinitialize
      initialValues={formInitialValues}
      validateOnChange={false}
      validateOnBlur={false}
      validate={(values) => {
        const errors = validatePreScanQuestionnaireForm(values, memberData?.gender);
        if (Object.keys(errors).length > 0) {
          toast.error('Please complete all required fields.');
        }
        return errors;
      }}
      onSubmit={handleSubmit}
    >
      <PreScanQuestionnaireFormFields
        fullName={fullName}
        dateOfBirth={dateOfBirth}
        gender={gender}
        phoneNumber={phoneNumber}
        email={email}
      />
    </Formik>
  );
};

export default PreScanQuestionnaireForm;
